import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import './App.css';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8002'
  : `${window.location.protocol}//${window.location.host}/api`;
const NO_MATCH = '__NO_MATCH__';
const LOCATION_FIELDS = [
  { key: 'country', label: 'Country', field: 'country', zoho_field: 'Billing_Country' },
  { key: 'headquarters_city', label: 'City', field: 'headquarters_city', zoho_field: 'Billing_City' },
  { key: 'postal_code', label: 'Postal Code', field: 'postal_code', zoho_field: 'Billing_Code' },
  { key: 'street_address', label: 'Street', field: 'street_address', zoho_field: 'Billing_Street' },
];

function App() {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('map');
  
  // Filters
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [partners, setPartners] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  // Dashboard data
  const [summary, setSummary] = useState({});
  const [recentSignals, setRecentSignals] = useState([]);
  const [topOpportunities, setTopOpportunities] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [deals, setDeals] = useState({ by_stage: [], recent: [] });
  const [dealsNeedingAttention, setDealsNeedingAttention] = useState([]);
  const [dataQuality, setDataQuality] = useState({ suggestions: [], summary: {} });
  const [dealMarkers, setDealMarkers] = useState([]);
  const [unmappedDeals, setUnmappedDeals] = useState([]);
  const [emailReviews, setEmailReviews] = useState([]);
  const [selectedReviewCandidates, setSelectedReviewCandidates] = useState({});
  const [reviewNotes, setReviewNotes] = useState({});
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [dealActivity, setDealActivity] = useState([]);
  const [hotDeals, setHotDeals] = useState([]);
  const [dealEmails, setDealEmails] = useState([]);
  const [searchingNews, setSearchingNews] = useState(false);
  const [newsSearchResult, setNewsSearchResult] = useState(null);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingField, setSavingField] = useState(false);
  const [creatingTaskFor, setCreatingTaskFor] = useState(null);
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [scanningSignals, setScanningSignals] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [locationEdits, setLocationEdits] = useState({});
  const [savingDealLocation, setSavingDealLocation] = useState(null);
  const [dealDetailPanel, setDealDetailPanel] = useState({
    dealId: null,
    loading: false,
    data: null,
    error: null
  });
  const [partnerKeywords, setPartnerKeywords] = useState({});
  const [newPartnerName, setNewPartnerName] = useState('');
  const [newPartnerKeywords, setNewPartnerKeywords] = useState('');
  const [savingKeywords, setSavingKeywords] = useState(false);
  const [rematchingEmails, setRematchingEmails] = useState(false);
  const [refreshingGeo, setRefreshingGeo] = useState(false);
  const [refreshingDealEmails, setRefreshingDealEmails] = useState(false);

  useEffect(() => {
    loadMapData();
    loadPartners();
    loadCompanies();
    loadCountries();
    loadPartnerKeywordSettings();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [selectedPartner, selectedCompany, selectedCountry]);

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/partners`);
      setPartners(res.data.partners);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/dashboard/companies`);
      setCompanyList(res.data.companies);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await axios.get(`${API_URL}/dashboard/countries`);
      setCountries(res.data.countries || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadPartnerKeywordSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings/partner-keywords`);
      setPartnerKeywords(res.data.keywords || {});
    } catch (error) {
      console.error('Error loading partner keywords:', error);
    }
  };

  const searchCompanyNews = async (companyName) => {
    setSearchingNews(true);
    setNewsSearchResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/dashboard/search-company-news?company=${encodeURIComponent(companyName)}&limit=5`
      );
      setNewsSearchResult(res.data);
      // Reload full dashboard data so signals list refreshes with newly created signals
      await loadDashboardData();
    } catch (error) {
      console.error('Error searching company news:', error);
    } finally {
      setSearchingNews(false);
    }
  };

  const handleCompanyChange = (companyName) => {
    setSelectedCompany(companyName || null);
    setNewsSearchResult(null);
    if (companyName) {
      searchCompanyNews(companyName);
    }
  };

  const loadMapData = async () => {
    try {
      const [dealsRes, unmappedRes] = await Promise.all([
        axios.get(`${API_URL}/map/deals`),
        axios.get(`${API_URL}/map/deals-unmapped`)
      ]);

      setDealMarkers(dealsRes.data);
      setUnmappedDeals(unmappedRes.data.deals || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading map data:', error);
      setLoading(false);
    }
  };

  const handleRefreshDealGeo = async (dealId) => {
    if (!dealId || refreshingGeo) return;
    setRefreshingGeo(true);
    try {
      const res = await axios.post(`${API_URL}/map/deals/${dealId}/refresh-geo`);
      await loadMapData();
      alert(res.data.message || 'Geolocation refreshed.');
    } catch (error) {
      console.error('Error refreshing deal geolocation:', error);
      alert('Failed to refresh geolocation.');
    } finally {
      setRefreshingGeo(false);
    }
  };

  const handleRefreshDealEmails = async (dealId) => {
    if (!dealId || refreshingDealEmails) return;
    setRefreshingDealEmails(true);
    try {
      const res = await axios.post(`${API_URL}/map/deals/${dealId}/refresh-emails`);
      await loadDashboardData();
      alert(res.data.message || 'Email intelligence refreshed.');
    } catch (error) {
      console.error('Error refreshing deal emails:', error);
      alert('Failed to refresh email matches.');
    } finally {
      setRefreshingDealEmails(false);
    }
  };

  const refreshEmailMatchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/dashboard/email-match-reviews?status=pending&limit=10`);
      setEmailReviews(res.data.reviews || []);
    } catch (error) {
      console.error('Error loading email match reviews:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedPartner) params.append('partner', selectedPartner);
      if (selectedCompany) params.append('company', selectedCompany);
      if (selectedCountry) params.append('country', selectedCountry);
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const activityParams = new URLSearchParams(params);
      activityParams.set('limit', 12);
      const activityQuery = activityParams.toString() ? `?${activityParams.toString()}` : '';
      const emailParams = new URLSearchParams(params);
      emailParams.set('limit', 25);
      const emailQuery = emailParams.toString() ? `?${emailParams.toString()}` : '';
      
      const [summaryRes, signalsRes, oppsRes, tasksRes, dealsRes, attentionRes, qualityRes, activityRes, hotRes, emailRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/summary${queryStr}`),
        axios.get(`${API_URL}/dashboard/recent-signals${queryStr}`),
        axios.get(`${API_URL}/dashboard/top-opportunities${queryStr}`),
        axios.get(`${API_URL}/dashboard/pending-tasks`),
        axios.get(`${API_URL}/dashboard/deals${queryStr}`),
        axios.get(`${API_URL}/dashboard/deals-needing-attention${queryStr}`),
        axios.get(`${API_URL}/dashboard/data-quality?limit=10`),
        axios.get(`${API_URL}/dashboard/deal-activity${activityQuery}`),
        axios.get(`${API_URL}/dashboard/hot-deals${activityQuery}`),
        axios.get(`${API_URL}/dashboard/deal-emails${emailQuery}`)
      ]);

      setSummary(summaryRes.data);
      setRecentSignals(signalsRes.data);
      setTopOpportunities(oppsRes.data);
      setPendingTasks(tasksRes.data);
      setDeals(dealsRes.data);
      setDealsNeedingAttention(attentionRes.data);
      setDataQuality(qualityRes.data);
      setDealActivity(activityRes.data.deals || []);
      setHotDeals(hotRes.data.deals || []);
      setDealEmails(emailRes.data.emails || []);
      await refreshEmailMatchReviews();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const getOpportunityColor = (score) => {
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'yellow';
    return 'orange';
  };

  const isClosedStage = (stage) => {
    if (!stage) return false;
    return stage.toLowerCase().startsWith('closed');
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return '#e74c3c';
    if (priority === 'medium') return '#f39c12';
    return '#95a5a6';
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'high') return '🔴';
    if (priority === 'medium') return '🟡';
    return '⚪';
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleString();
  };

  const handleSaveField = async (suggestion) => {
    if (!editValue.trim()) return;
    setSavingField(true);
    try {
      const params = new URLSearchParams({
        entity_type: suggestion.entity_type,
        entity_id: suggestion.entity_id,
        field: suggestion.field,
        value: editValue.trim(),
        zoho_field: suggestion.zoho_field || '',
        zoho_id: suggestion.zoho_id || '',
      });
      const res = await axios.put(`${API_URL}/dashboard/update-crm-field?${params.toString()}`);
      if (res.data.status === 'success' || res.data.status === 'partial') {
        setEditingSuggestion(null);
        setEditValue('');
        setDataQuality((prev) => ({
          ...prev,
          suggestions: prev.suggestions.filter(
            (item) => !(item.entity_id === suggestion.entity_id && item.field === suggestion.field)
          )
        }));
        loadDashboardData();
      }
      if (res.data.status === 'partial') {
        alert(`Saved locally but Zoho sync failed: ${res.data.zoho_error}`);
      }
    } catch (error) {
      console.error('Error saving field:', error);
      alert('Failed to save. Check console for details.');
    } finally {
      setSavingField(false);
    }
  };

  const handleLocationEditChange = (entityId, field, value) => {
    setLocationEdits((prev) => ({
      ...prev,
      [entityId]: { ...(prev[entityId] || {}), [field]: value }
    }));
  };

  const handleSaveLocation = async (suggestion) => {
    const edits = locationEdits[suggestion.entity_id] || {};
    const pending = LOCATION_FIELDS.filter((f) => edits[f.field] && edits[f.field].trim() !== '');
    if (pending.length === 0) {
      alert('Add at least one location value before saving.');
      return;
    }
    setSavingField(true);
    try {
      for (const locField of pending) {
        const params = new URLSearchParams({
          entity_type: suggestion.entity_type,
          entity_id: suggestion.entity_id,
          field: locField.field,
          value: edits[locField.field].trim(),
          zoho_field: locField.zoho_field,
          zoho_id: suggestion.zoho_id || '',
        });
        await axios.put(`${API_URL}/dashboard/update-crm-field?${params.toString()}`);
      }
      setLocationEdits((prev) => ({ ...prev, [suggestion.entity_id]: {} }));
      setDataQuality((prev) => ({
        ...prev,
        suggestions: prev.suggestions.filter(
          (item) =>
            !(item.entity_id === suggestion.entity_id &&
              pending.some((field) => field.field === item.field))
        )
      }));
      loadDashboardData();
    } catch (error) {
      console.error('Error saving location fields:', error);
      alert('Failed to save location data.');
    } finally {
      setSavingField(false);
    }
  };

  const handleSaveUnmappedLocation = async (deal) => {
    if (!deal.company_id) {
      alert('This deal is not linked to a CRM company yet.');
      return;
    }
    const edits = locationEdits[deal.company_id] || {};
    const payload = {};
    LOCATION_FIELDS.forEach((field) => {
      const value = edits[field.field];
      if (value && value.trim()) {
        payload[field.field] = value.trim();
      }
    });
    if (Object.keys(payload).length === 0) {
      alert('Please provide at least one location detail before saving.');
      return;
    }
    setSavingDealLocation(deal.id);
    try {
      const res = await axios.post(`${API_URL}/map/deals/${deal.id}/fix-location`, payload);
      if (res.data.status === 'partial' && res.data.zoho_error) {
        alert(`Location saved locally, but Zoho update failed: ${res.data.zoho_error}`);
      }
      setLocationEdits((prev) => ({ ...prev, [deal.company_id]: {} }));
      await loadMapData();
      await loadDashboardData();
    } catch (error) {
      console.error('Error saving unmapped deal location:', error);
      alert('Failed to save location. Please try again.');
    } finally {
      setSavingDealLocation(null);
    }
  };

  const openDealDetail = async (dealId) => {
    setDealDetailPanel({ dealId, loading: true, data: null, error: null });
    try {
      const res = await axios.get(`${API_URL}/map/deals/${dealId}`);
      setDealDetailPanel({ dealId, loading: false, data: res.data, error: null });
    } catch (error) {
      console.error('Error loading deal detail:', error);
      setDealDetailPanel({ dealId, loading: false, data: null, error: 'Failed to load deal details.' });
    }
  };

  const closeDealDetail = () => {
    setDealDetailPanel({ dealId: null, loading: false, data: null, error: null });
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API_URL}/dashboard/update-task?task_id=${taskId}&status=completed`);
      loadDashboardData();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleCreateTask = async (dealId, companyName) => {
    if (!newTaskDesc.trim()) return;
    setSavingTask(true);
    try {
      const params = new URLSearchParams({
        description: newTaskDesc.trim(),
        deal_id: dealId,
        priority: newTaskPriority,
      });
      if (newTaskDueDate) params.append('due_date', newTaskDueDate);
      await axios.post(`${API_URL}/dashboard/create-task?${params.toString()}`);
      setCreatingTaskFor(null);
      setNewTaskDesc('');
      setNewTaskPriority('medium');
      setNewTaskDueDate('');
      loadDashboardData();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task.');
    } finally {
      setSavingTask(false);
    }
  };

  const handleScanSignals = async () => {
    setScanningSignals(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API_URL}/dashboard/scan-signals?limit=5`);
      setScanResult(res.data);
      await loadDashboardData();
    } catch (error) {
      console.error('Error scanning signals:', error);
    } finally {
      setScanningSignals(false);
    }
  };

  const handleSelectReviewCandidate = (reviewId, dealId) => {
    setSelectedReviewCandidates((prev) => ({ ...prev, [reviewId]: dealId }));
  };

  const handleReviewNotesChange = (reviewId, value) => {
    setReviewNotes((prev) => ({ ...prev, [reviewId]: value }));
  };

  const handleResolveReview = async (reviewId) => {
    const selected = selectedReviewCandidates[reviewId];
    if (!selected) {
      setReviewError('Select a deal (or choose No Match) before resolving.');
      return;
    }
    const noMatch = selected === NO_MATCH;
    setSavingReview(true);
    setReviewError(null);
    try {
      const params = new URLSearchParams({ review_id: reviewId });
      if (noMatch) {
        params.append('resolution', 'no_match');
      } else {
        params.append('deal_id', selected);
      }
      if (reviewNotes[reviewId]) {
        params.append('notes', reviewNotes[reviewId]);
      }
      await axios.post(`${API_URL}/dashboard/email-match-reviews/resolve?${params.toString()}`);
      await refreshEmailMatchReviews();
      setSelectedReviewCandidates((prev) => ({ ...prev, [reviewId]: null }));
      setReviewNotes((prev) => ({ ...prev, [reviewId]: '' }));
    } catch (error) {
      console.error('Error resolving review:', error);
      setReviewError('Failed to resolve the review. Please try again.');
    } finally {
      setSavingReview(false);
    }
  };

  const handleSavePartnerKeywords = async () => {
    if (!newPartnerName.trim() || !newPartnerKeywords.trim()) return;
    setSavingKeywords(true);
    try {
      const payload = {
        partner: newPartnerName.trim(),
        keywords: newPartnerKeywords.split(',').map((kw) => kw.trim()),
      };
      const res = await axios.put(`${API_URL}/settings/partner-keywords`, payload);
      setPartnerKeywords(res.data.keywords || {});
      setNewPartnerName('');
      setNewPartnerKeywords('');
    } catch (error) {
      console.error('Error saving partner keywords:', error);
      alert('Failed to save keywords. Check console for details.');
    } finally {
      setSavingKeywords(false);
    }
  };

  const handleRematchEmails = async () => {
    if (rematchingEmails) return;
    setRematchingEmails(true);
    try {
      await axios.post(`${API_URL}/admin/rematch-emails?days=90`);
      await loadDashboardData();
      alert('Email matching rerun complete.');
    } catch (error) {
      console.error('Error rematching emails:', error);
      alert('Failed to rematch emails. Check console for details.');
    } finally {
      setRematchingEmails(false);
    }
  };

  const openDealCount = dealMarkers.filter((deal) => !isClosedStage(deal.stage)).length;
  const closedDealCount = dealMarkers.length - openDealCount;

  return (
    <div className="App">
      <header className="App-header">
        <h1>AMIP - Aquaculture Market Intelligence Platform</h1>
        <nav className="nav-tabs">
          <button 
            className={view === 'map' ? 'active' : ''} 
            onClick={() => setView('map')}
          >
            Map View
          </button>
          <button 
            className={view === 'dashboard' ? 'active' : ''} 
            onClick={() => setView('dashboard')}
          >
            CRM Dashboard
          </button>
          {view === 'dashboard' && (
            <>
              <select 
                value={selectedPartner || ''} 
                onChange={(e) => setSelectedPartner(e.target.value || null)}
                className="partner-filter"
              >
                <option value="">All Partners</option>
                {partners.map(p => (
                  <option key={p.name} value={p.name}>
                    {p.name} ({p.deals} deals)
                  </option>
                ))}
              </select>
              <select
                value={selectedCompany || ''}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="partner-filter"
              >
                <option value="">All Companies</option>
                {companyList.map(c => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={selectedCountry || ''}
                onChange={(e) => setSelectedCountry(e.target.value || null)}
                className="partner-filter"
              >
                <option value="">All Countries</option>
                {countries.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.deals} deals)
                  </option>
                ))}
              </select>
            </>
          )}
        </nav>
      </header>

      {view === 'map' && (
        <>
          <div className="map-container">
            {loading ? (
              <div className="loading">Loading map data...</div>
            ) : (
              <MapContainer
                center={[40.0, 20.0]}
                zoom={5}
                style={{ height: '600px', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {dealMarkers.map((deal) => {
                  const closed = isClosedStage(deal.stage);
                  const fillColor = closed
                    ? '#7f8c8d'
                    : (deal.location_source === 'city'
                      ? '#e67e22'
                      : (deal.location_source === 'country' ? '#d35400' : '#c0392b'));
                  const borderColor = closed ? '#bdc3c7' : '#ffffff';
                  return (
                    <CircleMarker
                      key={deal.id}
                      center={[deal.lat, deal.lon]}
                      radius={8}
                      fillColor={fillColor}
                      color={borderColor}
                      weight={1}
                      fillOpacity={0.9}
                      eventHandlers={{
                        click: () => openDealDetail(deal.id)
                      }}
                    >
                      <Popup>
                        <strong>{deal.name}</strong><br />
                        Company: {deal.company || 'Unknown'}<br />
                        Stage: {deal.stage || '—'}<br />
                        {deal.value ? `Value: $${(deal.value / 1000).toFixed(0)}K` : ''}<br />
                        Source: {deal.location_source}<br />
                        <button
                          className="detail-link"
                          onClick={(e) => { e.preventDefault(); openDealDetail(deal.id); }}
                        >
                          View details
                        </button>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            )}
          </div>

          {dealDetailPanel.dealId && (
            <div className="deal-detail-panel">
              <div className="detail-header">
                <h3>Deal Insights</h3>
                <button className="close-btn" onClick={closeDealDetail}>✕</button>
              </div>
              {dealDetailPanel.loading && <p>Loading deal details...</p>}
              {dealDetailPanel.error && <p className="error-text">{dealDetailPanel.error}</p>}
              {!dealDetailPanel.loading && !dealDetailPanel.error && dealDetailPanel.data && (() => {
                const summary = dealDetailPanel.data.summary || {};
                const emails = dealDetailPanel.data.emails || [];
                const emailStats = dealDetailPanel.data.email_stats || {};
                const inboundEmails = emails.filter((email) => email.direction === 'inbound');
                const outboundEmails = emails.filter((email) => email.direction === 'outbound');
                const neutralEmails = emails.filter((email) => email.direction !== 'inbound' && email.direction !== 'outbound');
                const inboundList = inboundEmails.length ? inboundEmails : neutralEmails;
                const outboundList = outboundEmails.length ? outboundEmails : neutralEmails;
                const signalsList = dealDetailPanel.data.signals || [];
                const relatedEmails = dealDetailPanel.data.related_emails || [];

                const renderEmailList = (list, emptyText, directionLabel) => (
                  list.length === 0 ? (
                    <p className="empty-text">{emptyText}</p>
                  ) : (
                    <ul className="detail-list">
                      {list.map((email) => (
                        <li key={`${directionLabel}-${email.id}`}>
                          <strong>{email.subject || '(No subject)'}</strong>
                          <span>{email.sent_at ? formatDateTime(email.sent_at) : '—'}</span>
                          <p>{email.snippet || 'No preview available.'}</p>
                        </li>
                      ))}
                    </ul>
                  )
                );

                return (
                  <>
                    <div className="detail-actions">
                      <button
                        className="secondary-btn"
                        onClick={() => handleRefreshDealGeo(summary.id)}
                        disabled={refreshingGeo}
                      >
                        {refreshingGeo ? '⏳ Improving geo...' : '🗺 Improve Geolocation'}
                      </button>
                      <button
                        className="secondary-btn"
                        onClick={() => handleRefreshDealEmails(summary.id)}
                        disabled={refreshingDealEmails}
                      >
                        {refreshingDealEmails ? '⏳ Matching emails...' : '📧 Improve Email Linking'}
                      </button>
                      {summary.company?.name && (
                        <button
                          className="secondary-btn"
                          onClick={() => searchCompanyNews(summary.company.name)}
                          disabled={searchingNews}
                        >
                          {searchingNews ? '⏳ Gathering intel...' : '🌐 Refresh Intelligence'}
                        </button>
                      )}
                    </div>
                    <div className="detail-metrics">
                      <span className="metric-badge">Linked emails: {emailStats.matched || 0}</span>
                      <span className={`metric-badge ${emailStats.pending ? 'warning' : ''}`}>
                        Pending review: {emailStats.pending || 0}
                      </span>
                      <span className="metric-badge neutral">Other relevant: {emailStats.related || 0}</span>
                    </div>
                    <div className="detail-grid">
                      <div className="detail-column">
                        <h4>Deal Snapshot</h4>
                        <p><strong>{summary.name}</strong></p>
                        <p>🏢 {summary.company?.name || 'Unknown company'}</p>
                        <p>Stage: {summary.stage || '—'}</p>
                        <p>Value: {summary.value ? `$${(summary.value / 1000).toFixed(0)}K` : '—'}</p>
                        <p>Expected Close: {summary.expected_close_date ? formatDateTime(summary.expected_close_date) : '—'}</p>
                        <p>Last Activity: {summary.last_activity ? formatDateTime(summary.last_activity) : '—'}</p>
                      </div>
                      <div className="detail-column">
                        <h4>Emails Sent (Outbound)</h4>
                        {renderEmailList(outboundList, 'No outbound emails yet.', 'outbound')}
                      </div>
                      <div className="detail-column">
                        <h4>Emails Received (Inbound)</h4>
                        {renderEmailList(inboundList, 'No inbound emails yet.', 'inbound')}
                      </div>
                      <div className="detail-column">
                        <h4>Non-email Signals</h4>
                        {signalsList.length === 0 ? (
                          <p className="empty-text">No news or intelligence yet.</p>
                        ) : (
                          <ul className="detail-list">
                            {signalsList.map((signal) => (
                              <li key={signal.id}>
                                <strong>{signal.type}</strong>
                                <span>{signal.detected_at ? formatDateTime(signal.detected_at) : '—'}</span>
                                <p>{signal.description || 'No description'}</p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="detail-section">
                      <h4>Other Relevant Emails ({relatedEmails.length})</h4>
                      {relatedEmails.length === 0 ? (
                        <p className="empty-text">No additional emails detected for this account.</p>
                      ) : (
                        <ul className="detail-list related-list">
                          {relatedEmails.map((email) => (
                            <li key={email.id}>
                              <strong>{email.subject || '(No subject)'}</strong>
                              <span>{email.sent_at ? formatDateTime(email.sent_at) : '—'} · {email.status}</span>
                              <p>{email.snippet || 'No preview available.'}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <div className="stats">
            <div className="stat-card">
              <h3>{dealMarkers.length}</h3>
              <p>Total Deals</p>
            </div>
            <div className="stat-card">
              <h3>{openDealCount}</h3>
              <p>Open / Active</p>
            </div>
            <div className="stat-card">
              <h3>{closedDealCount}</h3>
              <p>Closed Deals</p>
            </div>
          </div>

          <div className="unmapped-deals">
            <h3>Deals Without Map Placement</h3>
            {unmappedDeals.length === 0 ? (
              <p className="empty-text">All open deals are plotted on the map.</p>
            ) : (
              <div className="unmapped-list">
                {unmappedDeals.map((deal) => (
                  <div key={deal.id} className="list-item unmapped-item">
                    <strong>{deal.name}</strong>
                    <p>🏢 {deal.company || 'Unknown company'}</p>
                    <p>Stage: {deal.stage || '—'}</p>
                    <p className="reason">Reason: {deal.reason || 'Unknown'}</p>
                    {deal.company_id ? (
                      <div className="location-edit-panel tight">
                        <p>Add the missing location info so we can geocode and place this deal on the map.</p>
                        <div className="location-input-grid">
                          {LOCATION_FIELDS.map((field) => (
                            <input
                              key={`${deal.company_id}-${field.field}`}
                              type="text"
                              className="location-input"
                              placeholder={field.label}
                              value={
                                (locationEdits[deal.company_id]?.[field.field]) ??
                                (deal[field.field] || '')
                              }
                              onChange={(e) =>
                                handleLocationEditChange(deal.company_id, field.field, e.target.value)
                              }
                            />
                          ))}
                        </div>
                        <button
                          className="save-btn"
                          onClick={() => handleSaveUnmappedLocation(deal)}
                          disabled={savingDealLocation === deal.id}
                        >
                          {savingDealLocation === deal.id ? '⏳ Saving…' : '💾 Save & Plot'}
                        </button>
                      </div>
                    ) : (
                      <p className="empty-text">
                        Link this deal to a CRM company to provide location data.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {view === 'dashboard' && (
        <div className="dashboard">
          <div className="dashboard-summary">
            <div className="stat-card">
              <h3>{summary.companies || 0}</h3>
              <p>Companies</p>
            </div>
            <div className="stat-card">
              <h3>{summary.contacts || 0}</h3>
              <p>Contacts</p>
            </div>
            <div className="stat-card">
              <h3>{summary.deals || 0}</h3>
              <p>Deals</p>
            </div>
            <div className="stat-card">
              <h3>{summary.opportunities || 0}</h3>
              <p>Active Opps</p>
            </div>
            <div className="stat-card">
              <h3>{summary.pending_tasks || 0}</h3>
              <p>Pending Tasks</p>
            </div>
            <div className="stat-card highlight-card">
              <h3>{dealsNeedingAttention.length}</h3>
              <p>Deals w/o Tasks</p>
            </div>
            <div className="stat-card">
              <h3>{emailReviews.length}</h3>
              <p>Email Reviews</p>
            </div>
            <div className="stat-card">
              <h3>{hotDeals.length}</h3>
              <p>Hot Deals</p>
            </div>
          </div>

          <div className="dashboard-grid">
            <div className="dashboard-section">
              <div className="section-header-row">
                <h2>Recent Signals</h2>
                <button
                  className="scan-btn"
                  onClick={handleScanSignals}
                  disabled={scanningSignals}
                >
                  {scanningSignals ? '⏳ Scanning...' : '🔍 Scan for news'}
                </button>
              </div>
              {scanningSignals && (
                <div className="news-search-status searching">
                  🔍 Scanning industry news sources for recent signals...
                </div>
              )}
              {scanResult && !scanningSignals && (
                <div className="news-search-status done">
                  ✅ Scan complete — {scanResult.signals_created} new signals found
                </div>
              )}
              {searchingNews && (
                <div className="news-search-status searching">
                  🔍 Searching the web for news about {selectedCompany}...
                </div>
              )}
              {newsSearchResult && !searchingNews && (
                <div className="news-search-status done">
                  ✅ Found {newsSearchResult.articles_found} articles, extracted {newsSearchResult.signals_created} new signals for {newsSearchResult.company}
                </div>
              )}
              <div className="list-container">
                {recentSignals.length === 0 ? (
                  <div className="empty-state">
                    <p>No signals yet. Run news monitoring or process emails to see intelligence here.</p>
                    <code>curl -X POST "http://localhost:8002/admin/monitor-news?region=international&limit=10"</code>
                  </div>
                ) : (
                  recentSignals.map((signal) => (
                    <div key={signal.id} className="list-item">
                      <div className="list-item-header">
                        <span className="signal-type">{signal.signal_type}</span>
                        <span className="confidence">{(signal.confidence * 100).toFixed(0)}%</span>
                      </div>
                      <p className="description">{signal.description}</p>
                      <div className="meta">
                        {signal.company && <span>🏢 {signal.company}</span>}
                        {signal.farm && <span>🐟 {signal.farm}</span>}
                        {signal.source_url && (
                          <a href={signal.source_url} target="_blank" rel="noopener noreferrer" className="source-link">
                            🔗 Source
                          </a>
                        )}
                        <span className={`partner-tag ${(signal.partner || 'General').replace(' ', '-')}`}>
                          {signal.partner || 'General'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="dashboard-section">
              <h2>Top Opportunities</h2>
              <div className="list-container">
                {topOpportunities.length === 0 ? (
                  <div className="empty-state">
                    <p>No opportunities yet. Opportunities are created from your Zoho deals.</p>
                  </div>
                ) : (
                  topOpportunities.map((opp) => (
                    <div key={opp.id} className="list-item">
                      <div className="list-item-header">
                        <span className="opp-type">🏢 {opp.company}</span>
                        <span className="score" style={{ color: getOpportunityColor(opp.score) }}>
                          {(opp.score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <p className="description">{opp.type}</p>
                      <div className="meta">
                        <span className="status">{opp.status}</span>
                        <span className={`partner-tag ${(opp.partner || 'General').replace(' ', '-')}`}>
                          {opp.partner || 'General'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="dashboard-section">
              <h2>Pending Tasks</h2>
              <div className="list-container">
                {pendingTasks.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending tasks.</p>
                  </div>
                ) : (
                  pendingTasks.map((task) => (
                  <div key={task.id} className="list-item">
                    <div className="list-item-header">
                      <span className="task-type">{task.type}</span>
                      <span 
                        className="priority" 
                        style={{ color: getPriorityColor(task.priority) }}
                      >
                        {task.priority}
                      </span>
                    </div>
                    <p className="description">{task.description}</p>
                    <div className="meta">
                      {task.company && <span>🏢 {task.company}</span>}
                      {task.contact && <span>👤 {task.contact}</span>}
                      <span className={`partner-tag ${(task.partner || 'General').replace(' ', '-')}`}>
                        {task.partner || 'General'}
                      </span>
                      <button className="complete-btn" onClick={() => handleCompleteTask(task.id)}>
                        ✅ Done
                      </button>
                    </div>
                  </div>
                ))
                )}
              </div>
            </div>

            <div className="dashboard-section attention-section">
              <h2>⚠️ Deals Needing Attention</h2>
              <p className="section-subtitle">Open deals with no pending tasks — these need action to move forward</p>
              <div className="list-container">
                {dealsNeedingAttention.length === 0 ? (
                  <div className="empty-state">
                    <p>All open deals have active tasks. Nice work!</p>
                  </div>
                ) : (
                  dealsNeedingAttention.map((deal) => (
                    <div key={deal.id} className="list-item attention-item">
                      <div className="list-item-header">
                        <span className="deal-name">{deal.company}</span>
                        <span className="deal-value">{deal.value ? `$${(deal.value / 1000).toFixed(0)}K` : '—'}</span>
                      </div>
                      <p className="description">{deal.name}</p>
                      {creatingTaskFor === deal.id ? (
                        <div className="inline-edit task-create-form">
                          <input
                            type="text"
                            className="edit-input"
                            placeholder="Task description..."
                            value={newTaskDesc}
                            onChange={(e) => setNewTaskDesc(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateTask(deal.id, deal.company)}
                            autoFocus
                          />
                          <select value={newTaskPriority} onChange={(e) => setNewTaskPriority(e.target.value)} className="priority-select">
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                          <input
                            type="date"
                            className="date-input"
                            value={newTaskDueDate}
                            onChange={(e) => setNewTaskDueDate(e.target.value)}
                          />
                          <button
                            className="save-btn"
                            onClick={() => handleCreateTask(deal.id, deal.company)}
                            disabled={savingTask || !newTaskDesc.trim()}
                          >
                            {savingTask ? '⏳' : '💾'} Save
                          </button>
                          <button className="cancel-btn" onClick={() => setCreatingTaskFor(null)}>✕</button>
                        </div>
                      ) : (
                        <div className="meta">
                          <span className="stage">{deal.stage}</span>
                          <span className={`partner-tag ${(deal.partner || 'General').replace(' ', '-')}`}>
                            {deal.partner || 'General'}
                          </span>
                          <button className="fix-btn" onClick={() => setCreatingTaskFor(deal.id)}>
                            ➕ Create Task
                          </button>
                        </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="dashboard-section settings-section">
            <h2>🧠 Intelligence Settings</h2>
            <p className="section-subtitle">
              Teach AMIP new partner/product keywords so the AI can recognize domain-specific emails.
            </p>
            <div className="keyword-grid">
              {Object.keys(partnerKeywords).length === 0 && (
                <p className="empty-text">No custom keywords yet.</p>
              )}
              {Object.entries(partnerKeywords).map(([partner, keywords]) => (
                <div key={partner} className="keyword-card">
                  <strong>{partner}</strong>
                  <p>{(keywords || []).join(', ') || '—'}</p>
                </div>
              ))}
            </div>
            <div className="inline-edit">
              <input
                type="text"
                className="edit-input"
                placeholder="Partner (e.g., BossAqua)"
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
              />
              <input
                type="text"
                className="edit-input"
                placeholder="Keywords (comma separated)"
                value={newPartnerKeywords}
                onChange={(e) => setNewPartnerKeywords(e.target.value)}
              />
              <button
                className="save-btn"
                onClick={handleSavePartnerKeywords}
                disabled={savingKeywords}
              >
                {savingKeywords ? '⏳ Saving...' : '💾 Save Keywords'}
              </button>
            </div>
            <button
              className="secondary-btn"
              onClick={handleRematchEmails}
              disabled={rematchingEmails}
            >
              {rematchingEmails ? '⏳ Re-running matcher...' : '🔄 Re-run email matching'}
            </button>
          </div>

            <div className="dashboard-section">
              <h2>📧 Email Match Reviews</h2>
              <p className="section-subtitle">LLM couldn't confidently match these emails to deals. Confirm the right match so the CRM stays aligned.</p>
              {reviewError && (
                <div className="news-search-status searching">{reviewError}</div>
              )}
              <div className="list-container">
                {emailReviews.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending email reviews. Nice!</p>
                  </div>
                ) : (
                  emailReviews.map((review) => (
                    <div key={review.review_id} className="list-item review-item">
                      <div className="list-item-header">
                        <span className="task-type">{review.direction || 'email'}</span>
                        <span className="priority">
                          {review.match_confidence ? `${Math.round(review.match_confidence * 100)}%` : '—'}
                        </span>
                      </div>
                      <p className="description">{review.email_subject || '(No subject)'}</p>
                      <p className="meta-snippet">{review.email_snippet || 'No preview available.'}</p>
                      <div className="meta">
                        {review.counterparty_name && <span>👤 {review.counterparty_name}</span>}
                        {review.counterparty_email && <span>✉️ {review.counterparty_email}</span>}
                        <span className="partner-tag">Needs review</span>
                      </div>
                      <div className="candidate-list">
                        {(!review.candidate_data || review.candidate_data.length === 0) && (
                          <p>No suggested deals found. You can still log this as “No Match”.</p>
                        )}
                        {(review.candidate_data || []).map((candidate) => (
                          <label key={candidate.deal_id} className="candidate-option">
                            <input
                              type="radio"
                              name={`candidate-${review.review_id}`}
                              value={candidate.deal_id}
                              checked={selectedReviewCandidates[review.review_id] === candidate.deal_id}
                              onChange={() => handleSelectReviewCandidate(review.review_id, candidate.deal_id)}
                            />
                            <div>
                              <strong>{candidate.deal_name}</strong>
                              <p>🏢 {candidate.company_name}</p>
                              <p className="meta">
                                Score {(candidate.score * 100).toFixed(0)}% — {candidate.reason || 'heuristic match'}
                              </p>
                            </div>
                          </label>
                        ))}
                        <label className="candidate-option no-match-option">
                          <input
                            type="radio"
                            name={`candidate-${review.review_id}`}
                            value={NO_MATCH}
                            checked={selectedReviewCandidates[review.review_id] === NO_MATCH}
                            onChange={() => handleSelectReviewCandidate(review.review_id, NO_MATCH)}
                          />
                          <div>
                            <strong>🚫 No matching deal</strong>
                            <p>Log this email as general context without linking it to a specific deal.</p>
                          </div>
                        </label>
                      </div>
                      <div className="inline-edit">
                        <textarea
                          className="edit-input"
                          placeholder="Notes (optional)"
                          value={reviewNotes[review.review_id] || ''}
                          onChange={(e) => handleReviewNotesChange(review.review_id, e.target.value)}
                        />
                        <button
                          className="save-btn"
                          onClick={() => handleResolveReview(review.review_id)}
                          disabled={savingReview}
                        >
                          {savingReview ? '⏳ Resolving' : '✅ Resolve'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="dashboard-section">
              <h2>📈 Email Intelligence</h2>
              <p className="section-subtitle">See which deals are heating up (or cooling off) based on recent email activity.</p>
              <div className="list-container">
                {dealActivity.length === 0 ? (
                  <div className="empty-state">
                    <p>No email activity data yet. Process emails to populate this view.</p>
                  </div>
                ) : (
                  dealActivity.map((deal) => (
                    <div
                      key={deal.deal_id}
                      className={`list-item ${deal.needs_followup ? 'attention-item' : ''}`}
                      title={(deal.recent_emails || []).map((email) => {
                        const directionLabel = email.direction === 'inbound' ? 'Inbound (client)' : 'Outbound (us)';
                        return `${directionLabel} — ${formatDateTime(email.sent_at)}\n${email.subject || '(No subject)'}\n${email.snippet || ''}`;
                      }).join('\n\n')}
                    >
                      <div className="list-item-header">
                        <span className="deal-name">{deal.deal_name}</span>
                        <span className="deal-value">{deal.value ? `$${(deal.value / 1000).toFixed(0)}K` : '—'}</span>
                      </div>
                      <p className="description">{deal.company} • Stage {deal.stage}</p>
                      <div className="meta">
                        <span>📬 Emails (7d): {deal.emails_last_7d}</span>
                        <span>📡 Signals (30d): {deal.signals_last_30d}</span>
                        {deal.needs_followup && (
                          <span className="partner-tag warning">
                            Follow up {deal.days_since_last_outbound != null ? `(${deal.days_since_last_outbound}d)` : ''}
                          </span>
                        )}
                      </div>
                      <div className="meta">
                        <span>⬇️ Last inbound: {formatDateTime(deal.last_inbound_at)}</span>
                        <span>⬆️ Last outbound: {formatDateTime(deal.last_outbound_at)}</span>
                        <span>📝 {deal.latest_email?.subject || 'No recent email'}</span>
                      </div>
                      {deal.latest_signal?.type && (
                        <div className="meta">
                          <span>⚡ {deal.latest_signal.type}</span>
                          <span>{deal.latest_signal.description}</span>
                          <span>{formatDateTime(deal.latest_signal.detected_at)}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="dashboard-section">
              <h2>🔥 Top Email-Driven Deals</h2>
              <p className="section-subtitle">Most active deals over the last 10 days, weighted toward customer replies.</p>
              <div className="list-container">
                {hotDeals.length === 0 ? (
                  <div className="empty-state">
                    <p>No hot deals yet. Once emails flow, you’ll see the busiest ones here.</p>
                  </div>
                ) : (
                  hotDeals.map((deal, idx) => (
                  <div key={deal.deal_id} className="list-item">
                    <div className="list-item-header">
                      <span className="deal-name">#{idx + 1} {deal.deal_name}</span>
                      <span className="deal-value">Score {deal.hot_score.toFixed(1)}</span>
                    </div>
                    <p className="description">{deal.company} • Stage {deal.stage || '—'}</p>
                      <div className="meta">
                        <span>📥 Inbound: {deal.inbound_emails}</span>
                        <span>📤 Outbound: {deal.outbound_emails}</span>
                        <span>📅 Last email: {formatDateTime(deal.last_email_at)}</span>
                        {deal.partner && (
                          <span className={`partner-tag ${(deal.partner || 'General').replace(' ', '-')}`}>
                            {deal.partner}
                          </span>
                        )}
                      </div>
                      {deal.emails && deal.emails.length > 0 && (
                        <div className="meta-snippet">
                          {deal.emails.map((email, emailIdx) => (
                            <div key={`${deal.deal_id}-email-${emailIdx}`}>
                              <strong>{email.direction === 'inbound' ? '⬇️ Client' : '⬆️ Us'} • {formatDateTime(email.sent_at)}</strong>
                              <p>{email.subject || '(No subject)'}</p>
                              <p className="meta-snippet">{email.snippet || ''}</p>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))
              )}
              </div>
            </div>

            <div className="dashboard-section">
              <h2>📨 Deal Emails</h2>
              <p className="section-subtitle">Most recent emails touching your deals, with people and context.</p>
              <div className="list-container">
                {dealEmails.length === 0 ? (
                  <div className="empty-state">
                    <p>No deal emails ingested yet. Connect your mailbox to populate this feed.</p>
                  </div>
                ) : (
                  dealEmails.map((email) => (
                    <div key={email.email_id} className="list-item">
                      <div className="list-item-header">
                        <span className="task-type">{email.direction === 'inbound' ? '⬇️ Client' : '⬆️ Us'}</span>
                        <span className="priority">{formatDateTime(email.sent_at)}</span>
                      </div>
                      <p className="description">{email.subject || '(No subject)'}</p>
                      <p className="meta-snippet">{email.snippet || 'No preview available.'}</p>
                      <div className="meta">
                        {email.company && <span>🏢 {email.company}</span>}
                        {email.deal && <span>📂 {email.deal}</span>}
                        {email.contact && <span>👤 {email.contact}</span>}
                        {email.counterparty_name && (
                          <span>✉️ {email.counterparty_name} ({email.counterparty_email || 'unknown'})</span>
                        )}
                        {email.partner && (
                          <span className={`partner-tag ${(email.partner || 'General').replace(' ', '-')}`}>
                            {email.partner || 'General'}
                          </span>
                        )}
                        {email.country && (
                          <span className="country-tag">🌍 {email.country}</span>
                        )}
                      </div>
                      {email.participants && email.participants.length > 0 && (
                        <div className="meta participants-list">
                          {email.participants.map((participant, idx) => (
                            <span key={`${email.email_id}-participant-${idx}`}>
                              {participant.role}: {participant.name || participant.email || 'Unknown'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="dashboard-section">
              <h2>Deals Pipeline</h2>
              <div className="deals-summary">
                {deals.by_stage.map((stage) => (
                  <div key={stage.stage} className="deal-stage">
                    <h4>{stage.stage}</h4>
                    <p>{stage.count} deals</p>
                    <p className="deal-value">
                      ${(stage.total_value / 1000).toFixed(0)}K
                    </p>
                  </div>
                ))}
              </div>
              <div className="list-container">
                <h3>Recent Deals</h3>
                {deals.recent.map((deal) => (
                  <div key={deal.id} className="list-item">
                    <div className="list-item-header">
                      <span className="deal-name">{deal.name}</span>
                      <span className="deal-value">${(deal.value / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="meta">
                      <span>🏢 {deal.company}</span>
                      <span className="stage">{deal.stage}</span>
                      <span className={`partner-tag ${(deal.partner || 'General').replace(' ', '-')}`}>
                        {deal.partner || 'General'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-section data-quality-section">
              <h2>📋 CRM Data Quality</h2>
              <div className="quality-summary">
                <div className="quality-stat">
                  <span className="quality-count">{dataQuality.summary.missing_geo || 0}</span>
                  <span className="quality-label">Missing Geo Data</span>
                </div>
                <div className="quality-stat">
                  <span className="quality-count">{dataQuality.summary.missing_emails || 0}</span>
                  <span className="quality-label">Missing Emails</span>
                </div>
                <div className="quality-stat">
                  <span className="quality-count">{dataQuality.summary.missing_websites || 0}</span>
                  <span className="quality-label">Missing Websites</span>
                </div>
              </div>
              <div className="list-container">
                {dataQuality.suggestions.length === 0 ? (
                  <div className="empty-state">
                    <p>✅ All CRM data looks good!</p>
                  </div>
                ) : (
                  dataQuality.suggestions.map((suggestion, idx) => (
                    <div key={idx} className={`list-item quality-item priority-${suggestion.priority}`}>
                      <div className="list-item-header">
                        <span className="quality-entity">
                          {getPriorityIcon(suggestion.priority)} {suggestion.entity_name}
                        </span>
                        <span className="priority-badge" style={{ backgroundColor: getPriorityColor(suggestion.priority) }}>
                          {suggestion.priority}
                        </span>
                      </div>
                      <p className="description">{suggestion.message}</p>
                      {editingSuggestion === idx ? (
                        <div className="inline-edit">
                          <input
                            type="text"
                            className="edit-input"
                            placeholder={`Enter ${suggestion.field.replace('_', ' ')}...`}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveField(suggestion)}
                            autoFocus
                          />
                          <button
                            className="save-btn"
                            onClick={() => handleSaveField(suggestion)}
                            disabled={savingField || !editValue.trim()}
                          >
                            {savingField ? '⏳' : '💾'} {savingField ? 'Saving...' : 'Save to CRM'}
                          </button>
                          <button
                            className="cancel-btn"
                            onClick={() => { setEditingSuggestion(null); setEditValue(''); }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="meta">
                          <span>📝 {suggestion.field}</span>
                          {suggestion.company && <span>🏢 {suggestion.company}</span>}
                          {suggestion.country && <span>🌍 {suggestion.country}</span>}
                          <button
                            className="fix-btn"
                            onClick={() => { setEditingSuggestion(idx); setEditValue(''); }}
                          >
                            ✏️ Fix now
                          </button>
                        </div>
                      )}
                      {suggestion.type === 'missing_geo' && (
                        <div className="location-edit-panel">
                          <p>Need more precision? Update the full location info:</p>
                          <div className="location-input-grid">
                            {LOCATION_FIELDS.map((field) => (
                              <input
                                key={`${suggestion.entity_id}-${field.field}`}
                                type="text"
                                className="location-input"
                                placeholder={field.label}
                                value={
                                  (locationEdits[suggestion.entity_id]?.[field.field]) ||
                                  (suggestion[field.field] || '')
                                }
                                onChange={(e) =>
                                  handleLocationEditChange(suggestion.entity_id, field.field, e.target.value)
                                }
                              />
                            ))}
                          </div>
                          <button
                            className="save-btn"
                            onClick={() => handleSaveLocation(suggestion)}
                            disabled={savingField}
                          >
                            {savingField ? '⏳ Updating...' : '💾 Save Location'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
